"use client";

import { Mail } from "lucide-react";

import { AuthFormFooter } from "@/app/(discover)/components/auth/auth-form-footer";
import { formMode } from "@/components/auth/form-mode";
import GoogleLogo from "@/components/auth/google-logo";
import LastUsedIndicator from "@/components/auth/last-used-indicator";
import { Button } from "@/components/ui/button";
import useLastAuthMethod from "@/hooks/auth/use-last-auth-method";
import { useIsClient } from "@/hooks/use-is-client";
import {
  AUTH_AUDIENCES,
  type AuthAudience,
} from "@/lib/constants/auth-audiences";
import { AUTH_METHODS } from "@/lib/constants/auth-methods";
import { authTestIds } from "@/lib/data-test-ids/components.data-test-ids";

interface AuthMethodStepViewProps {
  mode: formMode;
  audience: AuthAudience;
  isLoading: boolean;
  onContinueWithGoogle: () => void;
  onContinueWithEmail: () => void;
  footer?: React.ReactNode;
}

export default function AuthMethodStepView({
  mode,
  audience,
  isLoading,
  onContinueWithGoogle,
  onContinueWithEmail,
  footer,
}: AuthMethodStepViewProps) {
  const isDistrictStaff = audience === AUTH_AUDIENCES.DISTRICT_STAFF;
  const lastAuthMethod = useLastAuthMethod((s) => s.lastAuthMethod);
  const isClient = useIsClient();
  const canShowLastUsedIndicator = isClient && mode === formMode.signin;
  const showGoogleIndicator =
    canShowLastUsedIndicator && lastAuthMethod === AUTH_METHODS.GOOGLE;
  const showOtpIndicator =
    canShowLastUsedIndicator && lastAuthMethod === AUTH_METHODS.OTP;

  return (
    <div className="space-y-3 sm:space-y-5 w-full">
      <div className="space-y-3 sm:space-y-4">
        <div data-auth-method-slot="google" className="relative">
          <Button
            type="button"
            variant="outline"
            onClick={onContinueWithGoogle}
            disabled={isLoading}
            className="w-full h-10 sm:h-11 text-sm font-medium rounded-lg"
            data-testid={authTestIds.CONTINUE_WITH_GOOGLE_BUTTON}
          >
            <GoogleLogo className="size-4" />
            {mode === formMode.signup
              ? "Sign up with Google"
              : "Sign in with Google"}
          </Button>
          {showGoogleIndicator && <LastUsedIndicator />}
        </div>

        <div data-auth-method-slot="otp" className="relative">
          <Button
            type="button"
            variant="outline"
            onClick={onContinueWithEmail}
            disabled={isLoading}
            className="w-full h-10 sm:h-11 text-sm font-medium rounded-lg"
            data-testid={authTestIds.CONTINUE_WITH_EMAIL_BUTTON}
          >
            <Mail />
            {mode === formMode.signup
              ? "Sign up with email"
              : "Sign in with email"}
          </Button>
          {showOtpIndicator && <LastUsedIndicator />}
        </div>
      </div>

      {!isDistrictStaff && (
        <AuthFormFooter showLegalLinks>{footer}</AuthFormFooter>
      )}
    </div>
  );
}
